/**
 * Shared access-token refresh logic.
 *
 * One module-level in-flight promise guarantees single-flight refresh across
 * the axios interceptor, the scheduled refresh hook, and any socket that
 * needs to re-auth. Uses raw fetch (not apiClient) so the 401 interceptor
 * cannot recurse into us.
 */

import { authUtils } from './auth'

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? '/api/v1' : 'http://localhost:8880/api/v1')

let inFlight: Promise<string | null> | null = null

/** Decode a JWT's `exp` claim (seconds since epoch). Returns null on any parse failure. */
export function decodeJwtExp(token: string): number | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
  const json = JSON.parse(atob(padded))
  return typeof json.exp === 'number' ? json.exp : null
}

/** Milliseconds until the current access token's exp; negative if already expired; null if no token. */
export function msUntilAccessTokenExpiry(): number | null {
  const token = authUtils.getToken()
  if (!token) return null
  const exp = decodeJwtExp(token)
  if (exp === null) return null
  return exp * 1000 - Date.now()
}

/**
 * Refresh the access token using the stored refresh token.
 * Returns the new access token on success, or null on failure.
 * Concurrent callers share a single in-flight request.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlight) return inFlight

  inFlight = (async () => {
    const refreshToken = authUtils.getRefreshToken()
    if (!refreshToken) return null

    const res = await fetch(`${API_BASE_URL}/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!res.ok) {
      // Refresh token is invalid/expired. Caller is responsible for logout UX.
      return null
    }

    const data = await res.json()
    if (!data?.access_token) return null

    authUtils.setToken(data.access_token)
    if (data.refresh_token) {
      authUtils.setRefreshToken(data.refresh_token)
    }
    return data.access_token as string
  })().finally(() => {
    inFlight = null
  })

  return inFlight
}
